import React, { useCallback, useEffect, useState } from 'react';
import { Card, Badge, Button, Form, Modal, Table, Row, Col } from 'react-bootstrap';
import API from '../services/api';
import { dateTime } from './adminShared';
import { useAuth } from '../context/AuthContext';
import Loading from './Loading';

const TIPO_NAME = { E31: 'Crédito Fiscal', E32: 'Consumo', E33: 'Nota de débito', E34: 'Nota de crédito' };
const fmt = (tipo, n) => `${tipo}${String(n).padStart(10, '0')}`;
const num = (n) => Number(n).toLocaleString('es-DO');
// The server stores DD-MM-YYYY (DGII's format); <input type="date"> speaks YYYY-MM-DD.
const toInputDate = (dmy) => (dmy ? dmy.split('-').reverse().join('-') : '');
const shownDate = (dmy) => (dmy ? dmy.replace(/-/g, '/') : '—');

// Same thresholds as the warning banners on the Facturas page.
const statusOf = (r) => {
  if (!r.configured) return { label: 'Sin rango', bg: 'danger', hint: 'No se pueden emitir e-CF de este tipo hasta configurarlo.' };
  if (r.expiresInDays !== null && r.expiresInDays < 0) return { label: 'Vencido', bg: 'danger', hint: 'La secuencia autorizada ya venció.' };
  if (r.remaining <= 0) return { label: 'Agotado', bg: 'danger', hint: 'Se usaron todos los números del rango.' };
  if (r.expiresInDays !== null && r.expiresInDays <= 60) return { label: `Vence en ${r.expiresInDays} días`, bg: 'warning', hint: 'Solicita un rango nuevo antes de que venza.' };
  if (r.remaining <= 10 || r.remaining / r.total <= 0.1) return { label: 'Quedan pocos', bg: 'warning', hint: 'Solicita un rango nuevo en la Oficina Virtual de DGII.' };
  return { label: 'En orden', bg: 'success' };
};

const UsageBar = ({ used, total, tone }) => {
  const pct = total ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div style={{ height: 6, borderRadius: 3, background: 'var(--mm-line)', minWidth: 80 }} title={`${num(used)} de ${num(total)} usados`}>
      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: tone === 'danger' ? 'var(--mm-red)' : tone === 'warning' ? 'var(--mm-yellow)' : 'var(--mm-cyan)' }} />
    </div>
  );
};

const emptyForm = { start: '', end: '', expiration: '', authorization: '', confirmed: false };

const AdminNcfRangesTab = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(null); // the range row being edited
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');

  // "Protocolo 514" = facturas E32 internas (CLAUDE.md). The screen deliberately does not say what it does: the owner's choice.
  // Only an owner sees the card or can switch it (the server refuses anyone else).
  const isOwner = !!useAuth()?.user?.isOwner;
  const [internalE32, setInternalE32] = useState(null);
  const [internalBusy, setInternalBusy] = useState(false);
  const [internalNote, setInternalNote] = useState(null); // { ok, text }
  useEffect(() => {
    if (!isOwner) return;
    API.get('/admin/settings').then(r => setInternalE32(!!r.data.internalE32Enabled)).catch(() => setInternalNote({ ok: false, text: 'No se pudo cargar esta opción.' }));
  }, [isOwner]);
  const toggleInternal = async (on) => {
    setInternalBusy(true); setInternalNote(null);
    try {
      const { data: res } = await API.put('/admin/settings', { internalE32Enabled: on });
      setInternalE32(!!res.internalE32Enabled);
      setInternalNote({ ok: true, text: res.message });
    } catch (err) {
      setInternalNote({ ok: false, text: err.response?.data?.message || 'No se pudo guardar.' });
    } finally { setInternalBusy(false); }
  };

  const load = useCallback(() => API.get('/admin/ncf-ranges')
    .then(r => setData(r.data))
    .catch(err => setError(err.response?.data?.message || 'No se pudieron cargar los rangos.')), []);
  useEffect(() => { load(); }, [load]);

  const open = (r) => {
    setEditing(r); setFormError('');
    setForm(r.configured
      ? { start: String(r.start), end: String(r.end), expiration: toInputDate(r.expiration), authorization: r.authorization || '', confirmed: false }
      // A tipo with no range yet: suggest carrying on after the last number issued.
      : { ...emptyForm, start: String((r.lastNumber || 0) + 1) });
  };
  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const start = parseInt(form.start, 10);
  const end = parseInt(form.end, 10);
  const numbersOk = Number.isInteger(start) && Number.isInteger(end) && start >= 1 && end >= start;
  const expiryOk = !editing?.carriesExpiry || !!form.expiration;
  const canSave = numbersOk && expiryOk && form.confirmed && !busy;

  const save = async () => {
    setBusy(true); setFormError('');
    try {
      const { data: res } = await API.put(`/admin/ncf-ranges/${editing.tipoEcf}`, {
        start, end, expiration: editing.carriesExpiry ? form.expiration : null, authorization: form.authorization,
      });
      setData(d => ({ ...d, ...res }));
      setNotice(res.message);
      setEditing(null);
    } catch (err) {
      setFormError(err.response?.data?.message || 'No se pudo guardar el rango.');
    } finally { setBusy(false); }
  };

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!data) return <Loading label="Cargando rangos..." />;

  return (
    <>
      <Card className="shadow-sm p-3 mb-3">
        <Card.Title>Rangos de e-NCF autorizados por DGII</Card.Title>
        <div className="small text-muted mb-3">
          Cada tipo de comprobante electrónico solo se puede emitir dentro del rango que DGII autorizó (Oficina Virtual → Solicitudes → Comprobantes Fiscales).
          Cuando un rango se agota o vence, registra aquí el nuevo. Los números ya emitidos nunca se reutilizan: un rango nuevo debe empezar después del último emitido.
        </div>
        {!data.applies && (
          <div className="alert alert-info">
            Este servidor usa el ambiente de <strong>pruebas (sandbox)</strong>, que no tiene límite de rango. Lo que guardes aquí queda registrado para producción, pero no se aplica en este servidor.
          </div>
        )}
        {notice && <div className="alert alert-success">{notice}</div>}
        <div className="table-responsive">
          <Table hover className="align-middle mb-0">
            <thead>
              <tr>
                <th>Tipo</th><th>Rango autorizado</th><th style={{ minWidth: 140 }}>Uso</th><th>Próximo número</th><th>Vence</th><th>Estado</th><th />
              </tr>
            </thead>
            <tbody>
              {data.ranges.map(r => {
                const st = statusOf(r);
                return (
                  <tr key={r.tipoEcf}>
                    <td><strong>{r.tipoEcf}</strong><div className="small text-muted">{TIPO_NAME[r.tipoEcf]}</div></td>
                    <td className="small" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {r.configured
                        ? <>{fmt(r.tipoEcf, r.start)}<br />{fmt(r.tipoEcf, r.end)}{r.authorization && <div className="text-muted" style={{ fontFamily: 'inherit' }}>Aut. {r.authorization}</div>}</>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      {r.configured ? (
                        <>
                          <UsageBar used={r.used} total={r.total} tone={st.bg} />
                          <div className="small text-muted mt-1">{num(r.used)} de {num(r.total)} · quedan {num(r.remaining)}</div>
                        </>
                      ) : <span className="text-muted small">{r.lastEncf ? `Último emitido: ${r.lastEncf}` : 'Sin emitir'}</span>}
                    </td>
                    <td className="small" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{r.nextEncf || '—'}</td>
                    <td className="small">{r.carriesExpiry ? shownDate(r.expiration) : <span className="text-muted" title="DGII no fija vencimiento para este tipo">No aplica</span>}</td>
                    <td><Badge bg={st.bg} title={st.hint}>{st.label}</Badge></td>
                    <td className="text-end">
                      <Button size="sm" variant={r.configured && st.bg === 'success' ? 'outline-secondary' : 'primary'} onClick={() => open(r)}>
                        {r.configured ? 'Editar' : 'Configurar'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        <div className="small text-muted mt-3">
          Cada cambio queda en el registro de Actividad con quién lo hizo. Los cambios se aplican de inmediato.
        </div>
      </Card>

      {isOwner && (
      <Card className="shadow-sm p-3 mb-3">
        <Card.Title>Protocolo 514</Card.Title>
        <div className="small text-muted mb-3">
          Configuración interna. Solo el propietario puede verla y cambiarla.
        </div>
        <Form.Check
          type="switch"
          id="protocolo-514-switch"
          disabled={internalE32 === null || internalBusy}
          checked={!!internalE32}
          onChange={e => { if (window.confirm(`¿${e.target.checked ? 'Activar' : 'Desactivar'} el Protocolo 514?`)) toggleInternal(e.target.checked); }}
          label={internalE32 ? 'Protocolo 514 activado' : 'Protocolo 514 desactivado'}
        />
        {internalNote && <div className={`small mt-2 ${internalNote.ok ? 'text-success' : 'text-danger'}`}>{internalNote.text}</div>}
      </Card>
      )}

      {editing && (
        <Modal show onHide={() => setEditing(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editing.configured ? 'Editar' : 'Configurar'} rango {editing.tipoEcf} — {TIPO_NAME[editing.tipoEcf]}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editing.lastEncf && (
              <div className="small text-muted mb-3">
                Último número emitido: <strong>{editing.lastEncf}</strong>. Un rango nuevo debe empezar en <strong>{fmt(editing.tipoEcf, editing.lastNumber + 1)}</strong> o después.
              </div>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Desde (número)</Form.Label>
                  <Form.Control type="number" min={1} value={form.start} onChange={e => set({ start: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Hasta (número)</Form.Label>
                  <Form.Control type="number" min={1} value={form.end} onChange={e => set({ end: e.target.value })} autoFocus />
                </Form.Group>
              </Col>
            </Row>
            {numbersOk && (
              <div className="small mb-3" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                {fmt(editing.tipoEcf, start)} – {fmt(editing.tipoEcf, end)} <span className="text-muted" style={{ fontFamily: 'inherit' }}>({num(end - start + 1)} números)</span>
              </div>
            )}
            <Row>
              {editing.carriesExpiry && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Vence</Form.Label>
                    <Form.Control type="date" value={form.expiration} onChange={e => set({ expiration: e.target.value })} />
                    <Form.Text className="text-muted">Fecha de vencimiento de la secuencia en la autorización.</Form.Text>
                  </Form.Group>
                </Col>
              )}
              <Col md={editing.carriesExpiry ? 6 : 12}>
                <Form.Group className="mb-3">
                  <Form.Label>No. de autorización <span className="text-muted">(opcional)</span></Form.Label>
                  <Form.Control maxLength={30} value={form.authorization} onChange={e => set({ authorization: e.target.value })} />
                </Form.Group>
              </Col>
            </Row>
            {!editing.carriesExpiry && <div className="small text-muted mb-3">DGII no fija fecha de vencimiento para {editing.tipoEcf}.</div>}
            <Form.Check id="ncf-confirm" type="checkbox" checked={form.confirmed} onChange={e => set({ confirmed: e.target.checked })}
              label="Confirmo que estos datos coinciden con la autorización de DGII. Un número fuera del rango autorizado es rechazado por DGII y queda consumido." />
            {formError && <div className="alert alert-danger mt-3 mb-0">{formError}</div>}
          </Modal.Body>
          <Modal.Footer>
            <span className="small text-muted me-auto">{editing.updatedBy ? `Último cambio: ${editing.updatedBy}, ${dateTime(editing.updatedAt)}` : ''}</span>
            <Button variant="outline-secondary" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button variant="primary" disabled={!canSave} onClick={save}>{busy ? 'Guardando...' : 'Guardar rango'}</Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default AdminNcfRangesTab;
